import React, { useEffect, useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Dashboard.css';

import useOrgJwt from '../hooks/useOrgJwt';
import { formatDate } from '../utils/dateFormatter';
import EmptyState from '../components/EmptyState';

const PAGE_SIZE = 25;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const TRACE_BASE = process.env.REACT_APP_TRACE_BASE_URL || '';

// Escape CSV field values
function escapeCsvField(value) {
  if (value == null) return '';
  const str = String(value);
  // If contains comma, quote, or newline, wrap in quotes and escape existing quotes
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function Dashboard() {
  const { user } = useUser();
  const { orgJwt, tenantId, role } = useOrgJwt();

  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (!orgJwt) return;
    let isMounted = true;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page,
          limit: PAGE_SIZE,
          ...(fromDate && { from: fromDate.toISOString() }),
          ...(toDate && { to: toDate.toISOString() })
        });
        const res = await fetch(`${API_BASE}/logs?${params}`, {
          headers: { Authorization: `Bearer ${orgJwt}` }
        });
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        const data = await res.json();
        const fetched = data.logs || data.items || data || [];
        if (isMounted) {
          setLogs(prev => page === 1 ? fetched : [...prev, ...fetched]);
          setHasMore(Array.isArray(fetched) && fetched.length === PAGE_SIZE);
        }
      } catch {
      } finally {
        isMounted && setLoading(false);
      }
    })();
    return () => { isMounted = false; };
  }, [page, fromDate, toDate, tenantId, orgJwt]);

  const resetAndRefetch = () => {
    setPage(1);
    setLogs([]);
  };

  const handleExportCsv = async () => {
    if (!orgJwt) return;
    setExporting(true);
    try {
      // Fetch all logs matching the current filters (no pagination limit)
      const params = new URLSearchParams({
        page: 1,
        limit: 10000, // Large limit to get all logs
        ...(fromDate && { from: fromDate.toISOString() }),
        ...(toDate && { to: toDate.toISOString() })
      });
      const res = await fetch(`${API_BASE}/logs?${params}`, {
        headers: { Authorization: `Bearer ${orgJwt}` }
      });
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const data = await res.json();
      const allLogs = data.logs || data.items || data || [];

      if (allLogs.length === 0) {
        alert('No logs to export');
        return;
      }

      // Build CSV content
      const headers = ['Timestamp', 'Tenant ID', 'Trace ID', 'Redacted Prompt', 'Redacted Response'];
      const rows = allLogs.map(log => [
        formatDate(log.timestamp, 'full'),
        log.tenant_id || '',
        log.trace_id || log.traceId || log.trace || '',
        log.redacted_prompt || '',
        log.redacted_response || ''
      ].map(escapeCsvField).join(','));

      const csvContent = [headers.join(','), ...rows].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.download = `iqledger-logs-${dateStr}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export failed:', e);
      alert('Failed to export logs. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const traceCell = (log) => {
    const tid = log.trace_id || log.traceId || log.trace || '';
    if (!tid) return '';
    const href = TRACE_BASE ? `${TRACE_BASE}${tid}` : null;
    const copy = async () => { try { await navigator.clipboard.writeText(tid); } catch {} };
    return (
      <span className="trace-cell">
        {href ? <a href={href} target="_blank" rel="noreferrer">{tid}</a> : tid}
        <button className="copy-button" onClick={copy}>Copy</button>
      </span>
    );
  };

  return (
    <section className="page dashboard">
      <div className="dashboard-header">
        <h1>Audit Logs</h1>
        <p className="dashboard-subtitle">Redacted prompt/response activity</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>From:</label>
          <DatePicker
            selected={fromDate}
            onChange={d => { setFromDate(d); resetAndRefetch(); }}
            placeholderText="Start date"
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label>To:</label>
          <DatePicker
            selected={toDate}
            onChange={d => { setToDate(d); resetAndRefetch(); }}
            placeholderText="End date"
            className="filter-input"
          />
        </div>
        {role === 'admin' && (
          <div className="filter-group">
            <label>Tenant:</label>
            <input
              type="text"
              value={tenantId}
              onChange={() => {}}
              placeholder="Tenant ID"
              className="filter-input"
              readOnly
            />
          </div>
        )}
        <div className="filter-group export-group">
          <button 
            className="export-btn" 
            onClick={handleExportCsv}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export as CSV'}
          </button>
        </div>
      </div>

      {loading && <p className="dashboard-loading">Loading logs…</p>}
      {!loading && logs.length === 0 && (
        <EmptyState
          icon="pi-file"
          title="No logs found"
          description="Try adjusting the date filters above, or send some logs via the SDK to get started. Check the docs for integration examples."
          action={
            <a href="/docs" className="empty-state-link">View SDK Documentation →</a>
          }
        />
      )}
      {!loading && logs.length > 0 && (
        <>
          <div className="log-table-wrapper">
            <table className="log-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Tenant</th>
                  <th>Prompt</th>
                  <th>Response</th>
                  <th>Trace ID</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, index) => (
                  <tr key={log.id || `${log.timestamp}-${index}`} onClick={() => setSelectedLog(log)}>
                    <td>{formatDate(log.timestamp, 'table')}</td>
                    <td>{log.tenant_id || ''}</td>
                    <td>{(log.redacted_prompt || '').slice(0, 50)}{(log.redacted_prompt || '').length > 50 ? '…' : ''}</td>
                    <td>{(log.redacted_response || '').slice(0, 50)}{(log.redacted_response || '').length > 50 ? '…' : ''}</td>
                    <td>{traceCell(log)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {hasMore && (
            <div className="pagination-controls">
              <button className="cta-button" onClick={() => setPage(p => p + 1)}>
                Load More
              </button>
            </div>
          )}
        </>
      )}

      {selectedLog && (
        <div className="log-modal">
          <div className="log-modal-content">
            <button className="close-button" onClick={() => setSelectedLog(null)}>×</button>
            <h2>Log Details</h2>
            <p><strong>Timestamp:</strong> {formatDate(selectedLog.timestamp, 'full')}</p>
            <p><strong>Tenant ID:</strong> {selectedLog.tenant_id || ''}</p>
            <p><strong>Trace ID:</strong> {selectedLog.trace_id || selectedLog.traceId || selectedLog.trace || ''}</p>
            <p><strong>Redacted Prompt:</strong></p>
            <pre>{selectedLog.redacted_prompt || ''}</pre>
            <p><strong>Redacted Response:</strong></p>
            <pre>{selectedLog.redacted_response || ''}</pre>
            {role === 'admin' && (
              <p><strong>Tokens:</strong> {selectedLog.token_count || 'N/A'}</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

export default Dashboard;
